import clsx from 'clsx';
import { Badge, Item, ItemContent, ItemDescription, ItemTitle } from '@monksflow/monks-ui';
import { useGetDownloadUrl } from '@/shared/hooks/useGetDownloadUrl';
import OptimizedImage from '@/shared/ui/OptimizedImage';
import { VideoPlayer } from '@/shared/ui/VideoPlayer';
import type { BiographySectionProps } from './BiographySection.types';
import { useTranslation } from 'react-i18next';

const MAX_HOBBIES_DISPLAYED = 3;

interface InfoItemProps {
  label: string;
  value: string;
}

const InfoItem = ({ label, value }: InfoItemProps) => (
  <Item size="sm" className="items-start bg-surface">
    <ItemContent>
      <ItemTitle>{label}</ItemTitle>
      {/* Item clamps a description to two lines. An income reads "$15,000-$40,000 (variable,
          based on gig economy and nano-influencer affiliate earnings)" - the tile exists to
          carry that, so it wraps. */}
      <ItemDescription className="line-clamp-none">{value}</ItemDescription>
    </ItemContent>
  </Item>
);

export const BiographySection = ({ persona, compact, action }: BiographySectionProps) => {
  const { t } = useTranslation('audiences');

  const heroImageId = persona.photoAssetId || '';
  const { downloadUrl: heroImage } = useGetDownloadUrl(heroImageId);
  const heroVideoId = persona.videoAssetId || '';
  const { downloadUrl: heroVideo } = useGetDownloadUrl(heroVideoId);

  const demographics = persona.demographicsData?.data;
  const segmentName = persona.segmentName || '';
  const personaTitle = persona.personaTitle || segmentName;
  const personaBrief = persona.personaBrief || '';
  const segmentShare = persona.insightsData?.data.segmentShare;

  const ageRange = demographics?.age || '';
  const urbanicity = demographics?.urbanicity || '';
  const income = demographics?.income || '';
  const interests = demographics?.interests || [];
  const hobbies =
    interests.length > 0
      ? interests.slice(0, MAX_HOBBIES_DISPLAYED).join(', ')
      : t('audienceFlowWidget.biographySection.notSpecified');

  const mediaClassName = clsx(
    'min-w-0 shrink-0 overflow-hidden rounded-xl border border-border-light',
    compact ? 'aspect-video' : 'sm:flex-1 aspect-square',
  );

  const segmentShareBadge = segmentShare ? (
    <Badge variant="secondary" roundness="full">
      {t('audienceFlowWidget.biographySection.percentOfMarket', {
        percent: Math.round(segmentShare),
      })}
    </Badge>
  ) : null;

  return (
    <section className={clsx('flex flex-col gap-xl', !compact && 'sm:flex-row sm:items-stretch')}>
      {heroVideo ? (
        <div className={mediaClassName}>
          <div className="h-full w-full [&>div]:h-full">
            <VideoPlayer
              videoSrc={heroVideo}
              autoPlay
              className="h-full w-full scale-110 object-cover"
            />
          </div>
        </div>
      ) : heroImage ? (
        <div className={mediaClassName}>
          <OptimizedImage
            src={heroImage}
            alt={t('audienceFlowWidget.biographySection.profileImage', { name: segmentName })}
            containerClassName="h-full w-full"
            className="h-full w-full object-cover object-top"
          />
        </div>
      ) : null}

      <div className="flex min-w-0 flex-col justify-center gap-md p-xl sm:flex-1">
        <div className="flex flex-col gap-xs">
          {/* The audience's own name heads the block; the segment it belongs to is the list's
              business, not the profile's - except where there is no name to show, and the
              segment is all that keeps the profile from opening headless. */}
          {personaTitle && <h2 className="type-display-md text-foreground">{personaTitle}</h2>}

          {(action || segmentShare) && (
            <div className="flex flex-wrap items-center gap-xs">
              {typeof action === 'function' ? (
                action(segmentShareBadge)
              ) : (
                <>
                  {action}
                  {segmentShareBadge}
                </>
              )}
            </div>
          )}
        </div>

        {personaBrief && <p className="type-body-lg text-foreground-alt">{personaBrief}</p>}

        <div className="grid grid-cols-2 gap-3">
          {ageRange && (
            <InfoItem label={t('audienceFlowWidget.biographySection.ageRange')} value={ageRange} />
          )}
          {urbanicity && (
            <InfoItem
              label={t('audienceFlowWidget.biographySection.locations')}
              value={urbanicity}
            />
          )}
          {income && (
            <InfoItem label={t('audienceFlowWidget.biographySection.incomeLevel')} value={income} />
          )}
          <InfoItem label={t('audienceFlowWidget.biographySection.hobbies')} value={hobbies} />
        </div>
      </div>
    </section>
  );
};
